import { jwtDecode } from 'jwt-decode';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { endpoints } from '@/API/endpoints';
import { PlannedInfo } from '@/components/planning/dto';
import Router from '@/components/router/Router';
import { AuthContext } from '@/context/AuthContext';
import { PlannedInfoContext } from '@/context/PlanningPageContext';
import { UserRoleContext } from '@/context/RoleContext';
import { UserInfoContext } from '@/context/UserInfoContext';
import { useApi } from '@/hooks/useApi/useApi';
import { useAuthTokens } from '@/hooks/useAuthTokens/useAuthTokens';
import { ApiDataType, IUserDetailBase, TokenType, userType } from '@/types';

import { OpenedBlocksContext } from './context/OpenedBlocks';

function App() {
	const [isAuth, setIsAuth] = useState<boolean>(false);
	const [plannedInfo, setPlannedInfo] = useState<PlannedInfo[] | null>(null);
	const [isBlockOpen, setIsBlockOpen] = useState({
		isProfileOpen: false,
		isVacationOpen: false,
		isDayOffOpen: false,
		isMentorOpen: true,
	});
	const [userInfoContext, setUserInfoContext] = useState<ApiDataType<
		userType,
		string[]
	> | null>(null);
	const [userDetailInfoContext, setUserDetailInfoContext] =
		useState<ApiDataType<IUserDetailBase, string[]> | null>(null);
	const [userRoleContext, setUserRoleContext] = useState<ApiDataType>({
		data: [],
		errorMessage: [],
	});

	const [isLoadingRole, setLoadingRole] = useState(true);

	const effectRan = useRef(false);

	const { fetchTokens } = useAuthTokens();
	const navigate = useNavigate();

	useEffect(() => {
		if (effectRan.current === false) {
			const refreshToken = localStorage.getItem('refreshToken');
			if (refreshToken) {
				const decodedRefreshToken = jwtDecode<TokenType>(refreshToken);
				if (Date.now() < decodedRefreshToken.exp * 1000) {
					fetchTokens();
					setIsAuth(true);
				} else {
					localStorage.removeItem('refreshToken');
					localStorage.removeItem('accessToken');
				}
			}

			return () => {
				effectRan.current = true;
			};
		}
	}, []);

	const { apiGet } = useApi();

	useLayoutEffect(() => {
		const getUserRole = async () => {
			setLoadingRole(true);
			const res = await apiGet(endpoints.userRoles);
			if (res.errorMessage.length > 0) {
				navigate('/403', {
					state: {
						code: 403,
						message: `${res.errorMessage[0]}

. Доступ ограничен!`,
					},
				});
			} else {
				setUserRoleContext(res);
			}
			setLoadingRole(false);
		};
		if (isAuth) getUserRole();
	}, [isAuth]);

	return (
		<AuthContext.Provider
			value={{
				isAuth,
				setIsAuth,
			}}
		>
			<OpenedBlocksContext.Provider value={{ isBlockOpen, setIsBlockOpen }}>
				<UserRoleContext.Provider value={{ userRoleContext, isLoadingRole }}>
					<UserInfoContext.Provider
						value={{
							userInfoContext,
							setUserInfoContext,
							userDetailInfoContext,
							setUserDetailInfoContext,
						}}
					>
						<PlannedInfoContext.Provider
							value={{ plannedInfo, setPlannedInfo }}
						>
							<Router />
						</PlannedInfoContext.Provider>
					</UserInfoContext.Provider>
				</UserRoleContext.Provider>
			</OpenedBlocksContext.Provider>
		</AuthContext.Provider>
	);
}

export default App;
